import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type WeatherDailyBucket = {
  date: string;
  temperature_min: number | null;
  temperature_max: number | null;
  precipitation_mm: number | null;
  sunshine_hours: number | null;
  cloud_cover_pct: number | null;
};

export type DashboardWeather = {
  site_id: string | null;
  latitude: number | null;
  longitude: number | null;
  by_day: WeatherDailyBucket[];
};

/**
 * Daily weather for the active site over the dashboard's date range, for the
 * waste-over-time overlay. Only runs when a single site is selected and the
 * range is bounded — weather is per-site and needs a start/end to fetch.
 */
export function useDashboardWeather(
  siteId: string | null,
  dateRange: { from: string | null; to: string | null },
) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = new URLSearchParams();
  if (siteId) params.set("site_id", siteId);
  if (dateRange.from) params.set("date_from", dateRange.from);
  if (dateRange.to) params.set("date_to", dateRange.to);

  return useQuery<DashboardWeather>({
    queryKey: ["dashboard-weather", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/dashboard/weather?${params.toString()}`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: DashboardWeather };
      return body.data;
    },
    enabled:
      !!siteId &&
      !!dateRange.from &&
      !!dateRange.to &&
      !!headers.Authorization.replace("Bearer ", ""),
    // No placeholderData: weather is site-specific, so previous data must not
    // linger when the selected site changes (or clears to "All sites").
  });
}
